import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle } from 'lucide-react';

const OrderConfirmation = () => {
  const location = useLocation();
  const order = location.state?.order;

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-light text-neutral-900 mb-4">Order not found</h2>
        <Link to="/products">
          <Button>Continue Shopping</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h1 className="text-3xl font-light text-neutral-900 mb-2">Order Confirmed!</h1>
        <p className="text-neutral-600">Thank you for your purchase from Freddy & Co.</p>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="font-light">Order Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-neutral-600">Order Number</p>
              <p className="font-semibold">{order.order_number}</p>
            </div>
            <div>
              <p className="text-neutral-600">Email</p>
              <p className="font-semibold">{order.customer_email}</p>
            </div>
          </div>
          <div>
            <p className="text-neutral-600 text-sm mb-2">Shipping Address</p>
            <div className="text-sm">
              <p>{order.shipping_address.full_name}</p>
              <p>{order.shipping_address.address_line1}</p>
              {order.shipping_address.address_line2 && <p>{order.shipping_address.address_line2}</p>}
              <p>{order.shipping_address.city}, {order.shipping_address.postal_code}</p>
              <p>{order.shipping_address.country}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="font-light">Items Ordered</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {order.items.map((item, idx) => (
            <div key={idx} className="flex justify-between">
              <div>
                <p className="font-semibold">{item.product_name}</p>
                <p className="text-sm text-neutral-600">
                  Qty: {item.quantity} {item.size && `• Size: ${item.size}`} {item.color && `• Color: ${item.color}`}
                </p>
              </div>
              <p className="font-semibold">£{(item.price * item.quantity).toFixed(2)}</p>
            </div>
          ))}
          <div className="border-t pt-3 mt-3">
            <div className="flex justify-between text-sm mb-1">
              <span className="text-neutral-600">Subtotal</span>
              <span>£{order.subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span className="text-neutral-600">Shipping</span>
              <span>{order.shipping === 0 ? 'FREE' : `£${order.shipping.toFixed(2)}`}</span>
            </div>
            <div className="flex justify-between text-lg font-semibold mt-2">
              <span>Total</span>
              <span>£{order.total.toFixed(2)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="bg-neutral-50 p-6 rounded-lg text-center">
        <p className="text-neutral-600 mb-4">
          A confirmation email has been sent to <strong>{order.customer_email}</strong>
        </p>
        <p className="text-sm text-neutral-500 mb-6">
          Your order will be processed within 1-2 business days. You'll receive a shipping notification once your items are on their way!
        </p>
        <Link to="/products">
          <Button variant="outline" size="lg">
            Continue Shopping
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default OrderConfirmation;
